import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { AuthGuard } from "../@core/guards/auth.guard";
import { InternalEntityGuard } from "../@core/guards/internal-entity.guard";
import { permissions, roles } from './shared/helpers/roles-permissions';
import { AppComponent } from './app.component';
import { HelpVideosComponent } from './modules/help-videos/help-videos.component';
import { AddEditEmploymentComponent } from './modules/employment/employment/add-edit-employment/add-edit-employment.component';
import { AddEditShiftComponent } from './modules/employment/employment/add-edit-shift/add-edit-shift.component';
import { FailScreenComponent } from './modules/modals/fail-screen/fail-screen.component';
import { SuccessScreenComponent } from './modules/modals/success-screen/success-screen.component';
import { SocietiesComponent } from './modules/societies/societies.component';
import { AvailablePermissionsComponent } from './modules/available-permissions/available-permissions.component';
import { AddEditJobComponent } from './modules/job/job/add-edit-job/add-edit-job.component';
import { CreateShiftComponent } from './modules/employment/employment/create-shift/create-shift.component';
import { OrderAndInvoicesComponent } from './modules/order-and-invoices/order-and-invoices/order-and-invoices.component';
import { ViewOrderComponent } from './modules/order-and-invoices/order-and-invoices/view-order/view-order.component';
import { AddEditProjectComponent } from './modules/job/job/add-edit-project/add-edit-project.component';
import { WorkOrderTableComponent } from './modules/enterprise-admin/work-order-table/work-order-table.component';
import { BookingsComponent } from './modules/societies/bookings/bookings.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { AgentsComponent } from './modules/agents/agents.component';
import { AgentViewComponent } from './modules/agents/agent-view/agent-view.component';
import { CreateAgentComponent } from './modules/agents/create-agent/create-agent.component';
import { RtspPreviewComponent } from './modules/agents/rtsp-preview/rtsp-preview.component';
import { PurchasedPhoneNumbersComponent } from './modules/phone-numbers/purchased-phone-numbers/purchased-phone-numbers.component';
import { WhatsAppAccountsComponent } from './modules/whatsapp/whatsapp-accounts/whatsapp-accounts.component';
import { AryaPageComponent } from '../@core/components/arya-chat/arya-page.component';

const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./public/public.module').then(m => m.PublicModule),
  },
  {
    path: 'profile',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule),
  },
  {
    path: 'job',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/job/job.module').then(m => m.JobModule),
    data: {
      permissions: [permissions.PROJECT_VIEW, permissions.PROJECT_CREATE, permissions.PROJECT_EDIT, permissions.PROJECT_DELETE,
      permissions.QUOTATION_VIEW, permissions.QUOTATION_VIEW, permissions.QUOTATION_VIEW, permissions.QUOTATION_VIEW,
      permissions.PO_VIEW, permissions.PO_VIEW, permissions.PO_VIEW, permissions.PO_VIEW,
      permissions.BILLING_VIEW, permissions.BILLING_VIEW, permissions.BILLING_VIEW, permissions.BILLING_VIEW,
      ]
    }
  },
  // {
  //   path: 'payments',
  //   canActivate: [AuthGuard],
  //   component: MainLayoutComponent,
  //   loadChildren: () => import('./modules/payments/payments.module').then(m => m.PaymentsModule),
  //   data: { roles: [roles.SUPER_ADMIN, roles.ON_BOARDING_STAFF] }
  // },
  {
    // Rate cards decide what every client is charged, so this is HOA staff only.
    // InternalEntityGuard enforces it; the screen and its nav entry are also gated
    // on entityType, so a client admin neither sees nor reaches it.
    path: 'billing',
    canActivate: [AuthGuard, InternalEntityGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/billing/billing.module').then(m => m.BillingModule),
  },
  {
    // Analysis debug logs carry prompts and full transcripts for every client, so
    // this is HOA staff only. InternalEntityGuard enforces it; the screen and its
    // nav entry are gated on entityType too, and the API re-checks server-side.
    path: 'call-analysis-debug',
    canActivate: [AuthGuard, InternalEntityGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/call-analysis-debug/call-analysis-debug.module').then(m => m.CallAnalysisDebugModule),
  },
  {
    path: 'admin',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
    // data: {
    //   permissions: [permissions.PROJECT_VIEW, permissions.PROJECT_CREATE, permissions.PROJECT_EDIT, permissions.PROJECT_DELETE,
    //   permissions.QUOTATION_VIEW, permissions.QUOTATION_VIEW, permissions.QUOTATION_VIEW, permissions.QUOTATION_VIEW,
    //   permissions.PO_VIEW, permissions.PO_VIEW, permissions.PO_VIEW, permissions.PO_VIEW,
    //   permissions.BILLING_VIEW, permissions.BILLING_VIEW, permissions.BILLING_VIEW, permissions.BILLING_VIEW,
    //   ]
    // }
  },
  {
    path: 'employer',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/employer/employer.module').then(m => m.EmployerModule),
  },
  {
    path: 'employment',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/employment/employment.module').then(m => m.EmploymentModule),
  },
  {
    path: 'enterprise-admin',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/enterprise-admin/enterprise-admin.module').then(m => m.EnterpriseAdminModule),
    data: {
      permissions: [permissions.WORKORDER_VIEW, permissions.WORKORDER_CREATE, permissions.WORKORDER_EDIT, permissions.WORKORDER_DELETE,
      permissions.TEAM_MEMBERS_VIEW, permissions.TEAM_MEMBERS_CREATE, permissions.TEAM_MEMBERS_EDIT, permissions.TEAM_MEMBERS_DELETE,
      permissions.CHAMPS_VIEW, permissions.CHAMPS_CREATE, permissions.CHAMPS_EDIT, permissions.CHAMPS_DELETE,
      permissions.ENTERPRISES_VIEW, permissions.ENTERPRISES_CREATE, permissions.ENTERPRISES_EDIT, permissions.ENTERPRISES_DELETE,
      permissions.PERMISSION_USERS_VIEW, permissions.PERMISSION_USERS_CREATE, permissions.PERMISSION_USERS_EDIT, permissions.PERMISSION_USERS_DELETE,
      permissions.VENDORS_VIEW, permissions.VENDORS_CREATE, permissions.VENDORS_EDIT, permissions.VENDORS_DELETE
      ]
    }
  },
  {
    path: 'job-seeker',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/job-seeker/job-seeker.module').then(m => m.JobSeekerModule),
    data: {
      permissions: [
        permissions.CHAMPS_VIEW, permissions.CHAMPS_CREATE, permissions.CHAMPS_EDIT, permissions.CHAMPS_DELETE
      ]
    }
  },
  {
    path: 'arya',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AryaPageComponent
      }
    ],
    data: {
      permissions: [permissions.PROJECT_VIEW]
    }
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
    data: {
      permissions: [
        permissions.DASHBOARD_VIEW, permissions.DASHBOARD_CREATE, permissions.DASHBOARD_EDIT, permissions.DASHBOARD_DELETE
      ]
    }
  },
  {
    path: 'order-and-invoices',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: OrderAndInvoicesComponent
      },
      {
        path: 'view-order/:id',
        component: ViewOrderComponent
      }
    ],
  },
  {
    path: 'invoices',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/invoices/invoices.module').then(m => m.InvoicesModule),
  },
  {
    path: 'help-videos',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: HelpVideosComponent
      }
    ],
  },
  {
    path: 'venues',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/societies/societies.module').then(m => m.SocietiesModule),
    data: {
      permissions: [
        permissions.VENUES_VIEW, permissions.VENUES_CREATE, permissions.VENUES_EDIT, permissions.VENUES_DELETE,
        permissions.BOOKINGS_VIEW, permissions.BOOKINGS_CREATE, permissions.BOOKINGS_EDIT, permissions.BOOKINGS_DELETE,
      ]
    }
  },
  {
    path: 'bookings',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: BookingsComponent
      }
    ],
    data: {
      permissions: [
        permissions.BOOKINGS_VIEW, permissions.BOOKINGS_CREATE, permissions.BOOKINGS_EDIT, permissions.BOOKINGS_DELETE,
      ]
    }
  },
  {
    path: 'available-permissions',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AvailablePermissionsComponent
      }
    ],
  },
  {
    path: 'attendance-tracker',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/attendance-tracker/attendance-tracker.module').then(m => m.AttendanceTrackerModule),
  },
  {
    path: 'payment-tracker',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/payment-tracker/payment-tracker.module').then(m => m.PaymentTrackerModule),
  },
  {
    path: 'supervisor-admin',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/supervisor-admin/supervisor-admin.module').then(m => m.SupervisorAdminModule),
  },
  {
    path: 'marketplace',
    // canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/marketplace/marketplace.module').then(m => m.MarketplaceModule),
  },
  {
    path: 'checkout/:id', component: AppComponent
  },
  {
    path: 'invoice-pay/:id',
    // canActivate: [AuthGuard],
    component: AppComponent
  },
  {
    path: 'fail-screen', component: FailScreenComponent
  },
  {
    path: 'success-screen', component: SuccessScreenComponent
  },
  {
    path: 'add-shift',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AddEditEmploymentComponent
      }
    ],
  },
  {
    path: 'add-edit-shift',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AddEditShiftComponent
      }
    ],
  },
  {
    path: 'create-shift',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: CreateShiftComponent
      }
    ],
  },
  {
    path: 'add-edit-job-template',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AddEditJobComponent
      }
    ],
  },
  {
    path: 'add-edit-project',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AddEditProjectComponent
      }
    ],
  },
  {
    path: 'add-edit-job-template/:id',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AddEditJobComponent
      }
    ],
  },
  {
    path: 'job-admin',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/job-admin/job-admin.module').then(m => m.JobAdminModule),
  },
  {
    path: 'credits',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/credits/credits.module').then(m => m.CreditsModule),
    data: {
      permissions: [
        permissions.CREDITS_VIEW, permissions.CREDITS_CREATE, permissions.CREDITS_EDIT, permissions.CREDITS_DELETE
      ]
    }
  },
  {
    // Legacy credits page called payment-service wallet/recharge endpoints that no longer
    // resolve (404 "Not Found" toast + blank table). Send everyone to the credits dashboard.
    path: 'credit-usage',
    pathMatch: 'full',
    redirectTo: 'credit-usage/credit-list'
  },
  {
    path: 'credit-usage/credit-list',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    data: {
      permissions: [
        permissions.CREDITS_VIEW, permissions.CREDITS_CREATE, permissions.CREDITS_EDIT, permissions.CREDITS_DELETE
      ]
    },
    loadChildren: () => import('./modules/credits/credits.module').then(m => m.CreditsModule),
  },
  {
    path: 'tasks',
    redirectTo: 'job/tasks',
    pathMatch: 'full'
  },
  {
    path: 'outcomes',
    redirectTo: 'job/outcomes',
    pathMatch: 'full'
  },
  {
    path: 'video-agent',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: RtspPreviewComponent
      }
    ],
  },
  {
    path: 'subscription-plans',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/subscription-plans/subscription-plans.module').then(m => m.SubscriptionPlansModule),
    data: {
      permissions: [
        permissions.SUBSCRIPTIONS_VIEW, permissions.SUBSCRIPTIONS_CREATE, permissions.SUBSCRIPTIONS_EDIT, permissions.SUBSCRIPTIONS_DELETE,
      ]
    }
  },
  {
    path: 'settings',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
    data: {
      permissions: [
        permissions.SETTINGS_VIEW, permissions.SETTINGS_CREATE, permissions.SETTINGS_EDIT, permissions.SETTINGS_DELETE
      ]
    }
  },
  {
    path: 'work-order-table',
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: WorkOrderTableComponent
      }
    ],
    data: {
      permissions: [
        permissions.WORKORDER_VIEW, permissions.WORKORDER_CREATE, permissions.WORKORDER_EDIT, permissions.WORKORDER_DELETE,
      ]
    }
  },
  {
    path: 'roles-permissions',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/roles-permissions/roles-permissions.module').then(m => m.RolesPermissionsModule),
    data: {
      permissions: [
        permissions.MEMBERS_AND_ROLES_VIEW, permissions.MEMBERS_AND_ROLES_CREATE, permissions.MEMBERS_AND_ROLES_EDIT, permissions.MEMBERS_AND_ROLES_DELETE,
      ]
    }
  },
  {
    path: 'referrals',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/referrals/referrals.module').then(m => m.ReferralsModule),
    data: {
      permissions: [
        permissions.REFERRAL_VIEW, permissions.REFERRAL_CREATE, permissions.REFERRAL_EDIT, permissions.REFERRAL_DELETE
      ]
    }
  },
  {
    path: 'phone-numbers',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    data: {
      permissions: [
        permissions.SETTINGS_VIEW, permissions.SETTINGS_CREATE, permissions.SETTINGS_EDIT, permissions.SETTINGS_DELETE
      ]
    },
    children: [
      {
        path: '',
        component: PurchasedPhoneNumbersComponent
      }
    ]
  },
  {
    path: 'whatsapp',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/whatsapp/whatsapp.module').then(m => m.WhatsAppModule),
    data: {
      permissions: [
        permissions.REFERRAL_VIEW
      ]
    }
  },
  {
    path: 'reports',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/reports/reports.module').then(m => m.ReportsModule),
    data: {
      permissions: [
        permissions.PROJECT_VIEW
      ]
    }
  },
  {
    path: 'digital-portfolio',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    loadChildren: () => import('./modules/digital-portfolio/digital-portfolio.module').then(m => m.DigitalPortfolioModule),
    data: {
      permissions: [
        permissions.DIGITAL_PORTFOLIO_VIEW, permissions.DIGITAL_PORTFOLIO_CREATE, permissions.DIGITAL_PORTFOLIO_EDIT, permissions.DIGITAL_PORTFOLIO_DELETE
      ]
    }
  },
  {
    path: 'agents',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AgentsComponent
      }
    ],
    data: {
      permissions: [
        permissions.AGENTS_VIEW
      ]
    }
  },
  {
    path: 'agents/agent-view/:id',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: AgentViewComponent
      }
    ],
    data: {
      permissions: [
        permissions.AGENTS_VIEW
      ]
    }
  },
  {
    path: 'agents/agent-create',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: CreateAgentComponent
      }
    ],
    data: {
      permissions: [
        permissions.AGENTS_VIEW
      ]
    }
  },
  {
    path: 'agents/agent-create/:id',
    canActivate: [AuthGuard],
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: CreateAgentComponent
      }
    ],
    data: {
      permissions: [
        permissions.AGENTS_VIEW
      ]
    }
  },
  // Wildcard route should be the last one
  {
    path: '**',
    component: MainLayoutComponent,
    children: [
      {
        path: '',
        component: PageNotFoundComponent
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // preloadingStrategy: PreloadAllModules,
    scrollPositionRestoration: 'enabled',
    relativeLinkResolution: 'corrected',
    anchorScrolling: 'enabled'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
